import { memo, useEffect, useRef, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Navigate, useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator } from '@shared/layouts'
import { useAudioStreamUpdateMutation } from 'hooks/mutations/content'
import { audioStreamsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Streams'
import { audioStreamRawRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Streams/Stream'
import { audioStreamQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { audiostreamId: streamId } = audioStreamRawRoute.useParams()
  const navigate = useNavigate({ from: audioStreamRawRoute.fullPath })

  const [saveAndExit, setSaveAndExit] = useState(false)

  const audioStreamQuery = useQuery(audioStreamQueryKeys.detail({ streamId }))
  const { data: audioStream } = audioStreamQuery

  const { mutate } = useAudioStreamUpdateMutation()

  useEffect(() => {
    if (editorInstanceRef.current && audioStreamQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(audioStream, null, 4))
    }
  }, [audioStreamQuery.isSuccess, audioStream])

  if (audioStreamQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!audioStream) {
    return <Navigate replace from={audioStreamRawRoute.fullPath} to={audioStreamsRoute.to} />
  }

  return (
    <Editor
      value={audioStream}
      onSubmit={(data) => {
        mutate(
          {
            streamId,
            audioStreamEdit: data,
          },
          {
            onSuccess: () => {
              enqueueSnackbar(t('domain:Workspace.Audio.audioStreamSaved'), {
                variant: 'success',
              })
              if (saveAndExit) {
                setSaveAndExit(false)
                navigate(audioStreamsRoute)
              }
            },
          },
        )
      }}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
